/**
 * idle-logoff - HIPAA automatic logoff (45 CFR 164.312(a)(2)(iii)).
 *
 * After a window of NO user activity, clears the MSAL token cache and redirects to
 * /login?reason=idle so member data is never left on an unattended device. Default 15 minutes.
 * Tunable at build time via VITE_IDLE_LOGOFF_MINUTES. Cross-tab via a localStorage last-activity
 * stamp.
 *
 * A full logoff is just clearing MSAL + redirecting to the login wall (no cdx_session cookie in
 * this standalone Entra-only app).
 */
import { useEffect } from 'react';
import { msalInstance, isDevAuthBypass } from '@/auth/index';

const DEFAULT_MINUTES = 15;
const LS_KEY = 'pickups_last_activity';
const RECORD_THROTTLE_MS = 5_000; // coalesce high-frequency events
const CHECK_INTERVAL_MS = 30_000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click', 'mousemove'] as const;

/** Resolve the idle window in ms from the build env, clamped to a 1-minute floor. Exported for tests. */
export function resolveIdleTimeoutMs(rawEnv: unknown, defaultMinutes = DEFAULT_MINUTES): number {
  const n = Number(rawEnv);
  const minutes = Number.isFinite(n) && n >= 1 ? n : defaultMinutes;
  return Math.floor(minutes) * 60_000;
}

/** Pure idle test. Exported for unit coverage. */
export function isIdle(lastActivityMs: number, nowMs: number, timeoutMs: number): boolean {
  return nowMs - lastActivityMs >= timeoutMs;
}

function readLastActivity(fallback: number): number {
  try {
    const v = Number(localStorage.getItem(LS_KEY));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeLastActivity(ts: number): void {
  try {
    localStorage.setItem(LS_KEY, String(ts));
  } catch {
    /* storage may be blocked */
  }
}

/** True only when there is a signed-in MSAL account to protect. */
function hasActiveSession(): boolean {
  try {
    return !!(msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]);
  } catch {
    return false;
  }
}

async function performLogoff(): Promise<void> {
  try {
    await msalInstance.clearCache();
  } catch {
    /* ignore */
  }
  try {
    window.location.assign('/login?reason=idle');
  } catch {
    /* ignore */
  }
}

/**
 * useIdleLogoff - arm the inactivity auto-logoff for the lifetime of the app. Mount once near the
 * root. Self-guards dev-bypass and only fires when an authenticated session exists.
 */
export function useIdleLogoff(): void {
  useEffect(() => {
    if (isDevAuthBypass()) return;
    const timeoutMs = resolveIdleTimeoutMs(import.meta.env.VITE_IDLE_LOGOFF_MINUTES);

    let lastRecord = 0;
    let firing = false;
    const record = () => {
      const t = Date.now();
      if (t - lastRecord < RECORD_THROTTLE_MS) return;
      lastRecord = t;
      writeLastActivity(t);
    };
    writeLastActivity(Date.now());
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, record, { passive: true });

    const id = window.setInterval(() => {
      if (firing) return;
      if (!hasActiveSession()) return;
      if (isIdle(readLastActivity(Date.now()), Date.now(), timeoutMs)) {
        firing = true;
        void performLogoff();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, record);
      window.clearInterval(id);
    };
  }, []);
}
